import axios from 'axios'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AlumniLoginAPI, AlumniSignUp } from '../../AllAPI/AdminAPI'

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --gold: #ffc107;
    --gold-deep: #e6a800;
    --gold-light: #fff3cd;
    --gold-pale: #fffbec;
    --dark: #1a1200;
    --dark-2: #2b1f00;
    --dark-3: #3d2e00;
    --text: #1a1200;
    --text-muted: #7a6000;
    --white: #ffffff;
    --shadow: 0 8px 40px rgba(255,193,7,0.18);
    --radius: 18px;
  }

  body {
    font-family: 'DM Sans', sans-serif;
    // background: var(--gold-pale);
    background: var(--gold-pale);
    min-height: 100vh;
  }

  .page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    // background: var(--gold-pale);
    position: relative;
    overflow: hidden;
  }

  /* Decorative background */
  .page::before {
    content: '';
    position: fixed;
    top: -120px; right: -120px;
    width: 480px; height: 480px;
    border-radius: 50%;
    // background: radial-gradient(circle, rgba(255,193,7,0.22) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
  .page::after {
    content: '';
    position: fixed;
    bottom: -80px; left: -80px;
    width: 380px; height: 380px;
    border-radius: 50%;
    // background: radial-gradient(circle, rgba(255,193,7,0.13) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  /* ─── HEADER ─── */
  .header {
    position: relative;
    z-index: 10;
    background: linear-gradient(135deg, var(#242443) 0%, var(#162e29) 60%, var(#162e29) 100%);
    // linear-gradient(115deg, #242443, #162e29)
    padding: 28px 40px 24px;
    display: flex;
    align-items: center;
    gap: 22px;
    box-shadow: 0 4px 32px rgba(255,193,7,0.18);
  }

  .logo-ring {
    width: 72px; height: 72px;
    border-radius: 50%;
    border: 3px solid var(--gold);
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(145deg, var(--dark-2), var(--dark-3));
    box-shadow: 0 0 0 6px rgba(255,193,7,0.12), 0 4px 20px rgba(0,0,0,0.4);
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
  }
  .logo-ring::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,193,7,0.18), transparent);
    animation: shimmer 3s infinite;
  }
  @keyframes shimmer {
    0% { left: -100%; }
    60%, 100% { left: 160%; }
  }

  .logo-icon {
    font-size: 32px;
  }

  .header-text h1 {
    font-family: 'Playfair Display', serif;
    font-size: 1.7rem;
    font-weight: 900;
    color: var(--gold);
    letter-spacing: 0.5px;
    line-height: 1.1;
  }
  .header-text p {
    font-size: 0.82rem;
    color: rgba(255,193,7,0.55);
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 3px;
    font-weight: 500;
  }

  .header-badge {
    margin-left: auto;
    background: rgba(255,193,7,0.12);
    border: 1px solid rgba(255,193,7,0.25);
    color: var(--gold);
    font-size: 0.72rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 50px;
    font-weight: 600;
  }

  /* ─── MAIN ─── */
  .main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    position: relative;
    z-index: 2;
  }

  .login-card {
    background: var(--white);
    border-radius: var(--radius);
    box-shadow: var(--shadow), 0 2px 0 var(--gold);
    padding: 44px 40px 36px;
    width: 100%;
    max-width: 420px;
    border: 1.5px solid rgba(255,193,7,0.18);
    position: relative;
    animation: cardIn 0.55s cubic-bezier(.22,1,.36,1) both;
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(32px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .card-accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    border-radius: var(--radius) var(--radius) 0 0;
    background: linear-gradient(90deg, var(--gold-deep), var(--gold), #ffe066, var(--gold));
    background-size: 200%;
    animation: gradMove 3s linear infinite;
  }
  @keyframes gradMove {
    0% { background-position: 0%; }
    100% { background-position: 200%; }
  }

  .card-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.55rem;
    font-weight: 700;
    color: var(--dark);
    text-align: center;
    margin-bottom: 4px;
  }
  .card-sub {
    text-align: center;
    font-size: 0.83rem;
    color: var(--text-muted);
    margin-bottom: 30px;
    letter-spacing: 0.5px;
  }

  .form-group {
    margin-bottom: 18px;
  }
  label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--dark);
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .input-icon {
    position: absolute;
    left: 14px;
    font-size: 1rem;
    pointer-events: none;
    color: var(--text-muted);
  }
  input[type="text"],
  input[type="password"] {
    width: 100%;
    padding: 12px 14px 12px 40px;
    border: 1.5px solid #e9d88a;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.95rem;
    color: var(--dark);
    background: var(--gold-pale);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  }
  input:focus {
    border-color: var(--gold);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(255,193,7,0.14);
  }

  .toggle-pass {
    position: absolute;
    right: 13px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    color: var(--text-muted);
    padding: 0;
    transition: color 0.15s;
  }
  .toggle-pass:hover { color: var(--gold-deep); }

  .row-options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    margin-top: 2px;
  }
  .remember {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.82rem;
    color: var(--text-muted);
    cursor: pointer;
  }
  .remember input[type="checkbox"] {
    accent-color: var(--gold-deep);
    width: 15px; height: 15px;
    cursor: pointer;
  }
  .forgot {
    font-size: 0.82rem;
    color: var(--gold-deep);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.15s;
  }
  .forgot:hover { color: var(--dark); }

   /* ─── BUTTONS ─── */
  .alp-btn-row {
    display: grid;
    grid-template-columns: 1fr 1.6fr;
    gap: 10px;
    margin-top: 6px;
  }
  .alp-btn-back {
    padding: 13px;
    background: transparent;
    border: 1.5px solid #e9d88a;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.93rem;
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .alp-btn-back:hover {
    border-color: var(--gold);
    color: var(--dark);
    background: var(--gold-pale);
  }
  .alp-btn-proceed {
    padding: 13px;
    background: linear-gradient(135deg, var(--gold-deep) 0%, var(--gold) 60%, #ffe066 100%);
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.97rem;
    font-weight: 700;
    color: var(--dark);
    cursor: pointer;
    letter-spacing: 0.4px;
    box-shadow: 0 4px 18px rgba(255,193,7,0.32);
    transition: transform 0.15s, box-shadow 0.15s, filter 0.15s;
    position: relative;
    overflow: hidden;
  }
  .alp-btn-proceed::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent);
    transition: left 0.4s;
  }
  .alp-btn-proceed:hover::after { left: 160%; }
  .alp-btn-proceed:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 26px rgba(255,193,7,0.42);
    filter: brightness(1.04);
  }
  .alp-btn-proceed:active { transform: translateY(0); }
  .alp-btn-proceed:disabled { opacity: 0.72; cursor: not-allowed; transform: none; }

  /* divider */
  .alp-divider {
    display: flex; align-items: center; gap: 10px; margin-top: 20px;
  }
  .alp-divider-line { flex: 1; height: 1px; background: #f0dfa0; }
  .alp-divider-text { font-size: 0.73rem; color: #b8a040; font-weight: 500; letter-spacing: 1px; }

  /* ─── FOOTER ─── */
  .footer {
    position: relative;
    z-index: 10;
    background:    linear-gradient(115deg, #242443, #162e29);
    // background: linear-gradient(135deg, var(#242443) 0%, var(#162e29) 60%, var(#162e29) 100%);
    color: rgba(255,255,255,0.7);
    padding: 36px 40px 22px;
    border-top: 2px solid var(--gold);
  }

  .footer-grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1.4fr;
    gap: 32px;
    max-width: 900px;
    margin: 0 auto 26px;
  }

  .footer-col h4 {
    font-family: 'Playfair Display', serif;
    font-size: 0.95rem;
    color: var(--gold);
    margin-bottom: 12px;
    letter-spacing: 0.5px;
  }

  .footer-col p, .footer-col a {
    font-size: 0.82rem;
    line-height: 1.7;
    color: rgba(255,255,255,0.6);
    text-decoration: none;
    display: block;
    transition: color 0.15s;
  }
  .footer-col a:hover { color: var(--gold); }

  .portal-link {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255,193,7,0.1);
    border: 1px solid rgba(255,193,7,0.25);
    color: var(--gold) !important;
    border-radius: 7px;
    padding: 7px 14px;
    font-size: 0.8rem !important;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
    transition: background 0.2s, border-color 0.2s !important;
  }
  .portal-link:hover {
    background: rgba(255,193,7,0.2) !important;
    border-color: rgba(255,193,7,0.5) !important;
    color: #fff !important;
  }

  .footer-bottom {
    border-top: 1px solid rgba(255,193,7,0.15);
    padding-top: 16px;
    text-align: center;
    font-size: 0.76rem;
    color: rgba(255,255,255,0.3);
    letter-spacing: 0.5px;
  }
  .footer-bottom span { color: var(--gold); }

  /* ─── ALERT ─── */
  .alert {
    border-radius: 9px;
    padding: 10px 14px;
    font-size: 0.82rem;
    margin-bottom: 16px;
    font-weight: 500;
    animation: fadeIn 0.3s;
  }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; } }
  .alert-error { background: #fff0f0; color: #c0392b; border: 1px solid #fac0c0; }
  .alert-success { background: #fffbe6; color: #7a6000; border: 1px solid var(--gold); }

  @media (max-width: 640px) {
    .header { padding: 18px 18px; gap: 14px; }
    .header-text h1 { font-size: 1.2rem; }
    .header-badge { display: none; }
    .login-card { padding: 32px 22px 26px; }
    .footer { padding: 28px 18px 16px; }
    .footer-grid { grid-template-columns: 1fr; gap: 20px; }
  }
`

export default function AlumniRegistrationPage() {
  const navigate = useNavigate()

  // ── Back ──
  const backToLogin = () => {
    sessionStorage.clear()
    navigate('/alumni/login')
  }

  const [formData, setFormData] = useState({
    f_name: '',
    m_name: '',
    l_name: '',
    email: '',
    password: '',
  })

  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const { f_name, m_name, l_name, email, password } = formData

    if (!f_name || !l_name || !email || !password) {
      toast.error('Please fill required fields')
      return
    }

      const payload = {
      ...formData,
      year: new Date().getFullYear(),
    }

    try {
      setLoading(true)
      const response = await axios.post(AlumniSignUp, payload)

      if (response.data === 'SAVED') {
        toast.success('Registration successful!')
        navigate('/alumni/login')
      } else if (response.data === 'EMAIL_ALREADY_EXIST') {
        toast.warning('Email already exists!')
      } else {
        toast.error('Something went wrong!')
      } 
    } catch (err) {
      toast.error('Error while saving')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* SAME STYLE (copy your style variable here) */}
      <style>{style}</style>

      <div className="page">
        {/* HEADER SAME */}
        <header className="header py-3 px-2 px-md-3">
          <div className="logo-ring">
            <span className="logo-icon">🏛️</span>
          </div>
          <div className="header-text">
            <h1>Digambar Jain Boarding, Kolhapur</h1>
            <p>Student Hostel & Accommodation</p>
          </div>
          <span className="header-badge">Est. 1905</span>
        </header>

        {/* MAIN */}
        <main className="main">
          <div className="login-card">
            <div className="card-accent" />

            <h2 className="card-title">Alumni Registration</h2>

            <form onSubmit={handleSubmit}>
              {/* First Name */}
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="f_name"
                  value={formData.f_name}
                  onChange={handleChange}
                />
              </div>

              {/* Middle Name */}
              <div className="form-group">
                <label>Middle Name</label>
                <input
                  type="text"
                  name="m_name"
                  value={formData.m_name}
                  onChange={handleChange}
                />
              </div>

              {/* Last Name */}
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="l_name"
                  value={formData.l_name}
                  onChange={handleChange}
                />
              </div>

              {/* Email */}
              <div className="form-group">
                <label>Email</label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              {/* Password */}
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>

              <div className="alp-btn-row">
                <button
                  type="button"
                  className="alp-btn-back"
                  onClick={backToLogin}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  className="alp-btn-proceed"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Register →'}
                </button>
              </div>

              <div
                className="row-options text-center"
                style={{ marginTop: 15 }}
              >
                <a href="#/alumni/login" className="forgot">
                  Already have account? Login
                </a>
              </div>
            </form>
          </div>
        </main>

        {/* ── FOOTER ── */}
        <footer className="footer">
          <div className="footer-grid">
            <div className="footer-col">
              <h4>🏛️ Digambar Jain Boarding, Kolhapur</h4>
              <p>
                Providing safe, comfortable &amp; affordable accommodation to
                students since 1905. Your home away from home.
              </p>
            </div>

            <div className="footer-col">
              <h4>🔐 Staff Portals</h4>
              <a href="#/admin/login" className="portal-link">
                ⚙️ Admin Login
              </a>
              <a href="#/student/login" className="portal-link">
                ⚙️ Student Login
              </a>
            </div>

            <div className="footer-col">
              <h4>📍 Contact Us</h4>
              <p>📞 +91 9851071008, 0233-2623603</p>
              <a href="mailto:dbjainsabha@gmail.com">
                ✉️ dbjainsabha@gmail.com
              </a>
              <p style={{ marginTop: '6px' }}>
                🏠 Near, Dasara Chowk, New Shahupuri,
                <br />
                Kolhapur, Maharashtra 416002
              </p>
            </div>
          </div>

          <div className="footer-bottom">
            © {new Date().getFullYear()}{' '}
            <span>Digambar Jain Boarding, Kolhapur</span>. All rights reserved.
            &nbsp;|&nbsp; Designed by TechnoAarv Solution.
          </div>
        </footer>
      </div>
    </>
  )
}
